import React from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

/**
 * App-level error boundary. Catches sync render errors so the user sees a
 * polished card instead of the CRA red overlay. Logs to console; in
 * production this would forward to Sentry/etc.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, info?.componentStack);
  }

  reset = () => this.setState({ hasError: false, err: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.err?.message || "Something unexpected happened.";
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-lg bg-card border border-border rounded-md p-8 shadow-xl">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-md bg-destructive/15 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl tracking-tight">Something broke</h2>
              <p className="text-sm text-muted-foreground mt-1">
                We hit an unexpected error rendering this page. Your data is safe —
                refresh to try again. If this keeps happening, send us the message
                below.
              </p>
              <pre className="mt-4 bg-secondary text-foreground/80 text-xs mono p-3 rounded-md overflow-auto max-h-40 whitespace-pre-wrap">
{msg}
              </pre>
              <div className="mt-5 flex gap-2 justify-end">
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
                >
                  <RotateCw className="w-3.5 h-3.5" /> Reload page
                </button>
                <button
                  onClick={this.reset}
                  className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
